<template>
  <div>
    <loading-spinner :loading-status="loadingStatus" />
    <div
      v-if="template.id && !loadingStatus"
      class="row"
    >
      <div class="col-sm-6">
        <Card
          icon="server"
          title="General"
        >
          <table>
            <tr v-if="activeType === 'saved'">
              <td>Status</td>
              <td>
                <div class="float-left">
                  <div
                    id="statusCustomOpen"
                    ref="statusDiv"
                    v-closable="{
                      exclude: ['statusDiv'],
                      handler: 'closeTooltip'
                    }"
                    class="custom-tooltip"
                    @click="onCustomOpen"
                  >
                    <ejs-tooltip
                      id="templateStatusTooltip"
                      ref="tooltip"
                      :content="statusToolTip"
                      css-class="customtip"
                      position="BottomCenter"
                      opens-on="Custom"
                    >
                      <span v-if="template.active">
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="success"
                          transform="shrink-3"
                        />
                        Active
                        <font-awesome-icon
                          :icon="['far', 'chevron-down']"
                          size="xs"
                        />
                      </span>
                      <span v-else>
                        <font-awesome-icon
                          :icon="['fas', 'circle']"
                          size="xs"
                          class="danger"
                          transform="shrink-3"
                        />
                        Inactive
                        <font-awesome-icon
                          :icon="['far', 'chevron-down']"
                          size="xs"
                        />
                      </span>
                    </ejs-tooltip>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td>Name</td>
              <td>{{ template.name }}</td>
            </tr>
            <tr>
              <td>Editor</td>
              <td>
                {{ editorTypeDescription }}
              </td>
            </tr>
            <tr v-if="activeType === 'saved'">
              <td>Campaigns</td>
              <td>
                <router-link
                  v-if="summary.childTemplates > 0"
                  class="nav-link link"
                  :to="{name: 'TemplateDetailsCampaigns', query: templateQuery}"
                >
                  {{ summary.childTemplates }}
                </router-link>
                <span v-else>{{ summary.childTemplates }}</span>
              </td>
            </tr>
            <tr>
              <td>Tags</td>
              <td v-if="summary.tags">
                {{ summary.tags.map(x => x.name).join(', ') }}
              </td>
            </tr>
            <tr>
              <td colspan="2">
                Description
              </td>
            </tr>
            <tr>
              <td
                colspan="2"
                class="gray"
              >
                {{ template.description }}
              </td>
            </tr>
          </table>
        </Card>
        <Card
          v-if="properties.length && activeType === 'saved'"
          icon="hotel"
          title="Properties"
        >
          <ul class="properties">
            <li
              v-for="item in properties"
              :key="item.id"
            >
              {{ item.name }}
            </li>
          </ul>
        </Card>
      </div>
      <div class="col-sm-6">
        <Card
          icon="clock"
          title="Key History"
          router-link="View all"
          router-link-name="TemplateDetailsHistory"
        >
          <History :items="history" />
        </Card>
      </div>
    </div>
    <div v-if="accountId && !template.id && !loadingStatus">
      <div class="alert alert-danger">
        <span>
          <font-awesome-icon :icon="['far', 'info-circle']" /> This template doesn't exist, please contact Product Help for assistance.
        </span>
      </div>
    </div>
    <BaseDialog
      ref="deactivateDialog"
      :content="'Are you sure you wish to deactivate this template? <br /> You will not be able to use within an email campaign unless the template is active.'"
      :submit-button="deactivateButton"
    />
    <BaseDialog
      ref="activateDialog"
      :content="'Are you sure you wish to activate this template?'"
      :submit-button="activateButton"
    />
  </div>
</template>

<script>
import Card from '@/components/Card'
import History from '@/components/History/index'
import { mapState, mapGetters } from 'vuex'
import { iframeParentCommunication } from '@/mixins/iframeParentCommunication'
import LoadingSpinner from '@/components/LoadingSpinner'
import BaseDialog from '@/components/Dialog/BaseDialog'
import StatusTooltip from '@/components/Tooltips/StatusTooltip'

export default {
  components: {
    Card,
    History,
    BaseDialog,
    LoadingSpinner
  },
  mixins: [iframeParentCommunication],
  data () {
    return {
      statusToolTip () {
        return {
          template: StatusTooltip
        }
      },
      templateQuery: {
        externalId: this.$route.query.externalId,
        clientId: this.$route.query.clientId,
        templateId: this.$route.query.templateId,
        userEmail: this.$route.query.userEmail
      },
      deactivateButton: {
        label: 'Deactivate',
        class: 'btn-danger',
        action: this.deactivateAction
      },
      activateButton: {
        label: 'Activate',
        class: 'btn-success',
        action: this.activateAction
      }
    }
  },
  computed: {
    ...mapState({
      template: state => state.templateBuilder.template.summary.template ?? {},
      accountIds: state => state.templateBuilder.template.summary.accountIds ?? [],
      summary: state => state.templateBuilder.template.summary,
      history: state => state.templateBuilder.template.history.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5),
      editorsTypes: state => state.templateBuilder.editorsTypes,
      accountId: state => state.templateBuilder.accountId
    }),
    ...mapGetters({
      availableProperties: 'getAvailableAccounts',
      activeType: 'getActiveType'
    }),
    properties () {
      const properties = []
      for (const i in this.accountIds) {
        const property = this.availableProperties.find(item => item.id === this.accountIds[i])
        if (property) {
          properties.push({ ...property })
        }
      }
      return properties
    },
    loadingStatus () {
      return this.$store.getters.getLoadingStatusItem('set_template_summary')
    },
    propertiesLoadingStatus () {
      return this.$store.getters.getLoadingStatusItem('get_available_properties')
    },
    editorTypeDescription () {
      if (this.template && this.editorsTypes && this.template.editorTypeId) {
        return this.editorsTypes.find(e => e.id === this.template.editorTypeId)?.description
      }
      return null
    }
  },
  watch: {
    summary (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.$store.commit('SET_GRIDSELECTEDTEMPLATE', newVal.template)
      }
    },
    accountId () {
      this.init()
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    init () {
      if (this.accountId) {
        const clientId = this.$route.query.clientId
        const templateId = this.$route.query.templateId
        const userEmail = atob(this.$route.query.userEmail)
        if (clientId && templateId) {
          if (this.activeType === 'saved') {
            if (Object.keys(this.template).length === 0) this.$store.dispatch('setTemplateSummary', { templateId, userEmail, showLoading: true })
            if (this.history.length === 0) this.$store.dispatch('setTemplateHistory', { templateId, userEmail, showLoading: true })
            this.$store.dispatch('getAvailableProperties', { clientId, userEmail })
          } else if (this.activeType === 'prebuilt') {
            if (Object.keys(this.template).length === 0) this.$store.dispatch('setPrebuiltTemplateSummary', { templateId, userEmail, showLoading: true })
            if (this.history.length === 0) this.$store.dispatch('setPrebuiltTemplateHistory', { templateId, userEmail, showLoading: true })
          } else {
            console.log('ERROR')
          }
        }
      }
    },
    onCustomOpen (args) {
      if (args.target.getAttribute('data-tooltip-id')) {
        this.$refs.tooltip.close()
      } else {
        this.$refs.tooltip.open(args.target)
      }
    },
    closeTooltip () {
      if (this.$refs.tooltip) {
        this.$refs.tooltip.close()
      }
    },
    refreshTemplates () {
      const templateId = this.$route.query.templateId
      const userEmail = atob(this.$route.query.userEmail)
      const showLoading = false
      if (templateId) {
        this.$store.dispatch('setTemplates', { userEmail, showLoading })
        this.$store.dispatch('setTemplateSummary', { templateId, userEmail, showLoading })
        this.$store.dispatch('setTemplateHistory', { templateId, userEmail, showLoading })
      }
    },
    deactivateAction () {
      this.changeStatusTemplate(false)
    },
    activateAction () {
      this.changeStatusTemplate(true)
    },
    changeStatusTemplate (state) {
      const encodedUrlQuery = this.$helpers.getTemplateBuilderQuery(this.$route, false)
      const unencodedUrlQuery = { ...encodedUrlQuery }
      const userEmail = atob(unencodedUrlQuery.userEmail)
      const templateId = this.$route.query.templateId
      const response = this.$store.dispatch('activeTemplate', { templateId, active: state, userEmail })
      response.then(({ success, error }) => {
        if (success) {
          this.refreshTemplates()
        } else if (error) {
          console.log(error)
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
  .e-control {
    font-size: 1.6rem;
    font-family: 'Source Sans Pro';
  }
  .link {
    color: $secondary;
    text-decoration: underline;
    &:hover {
      cursor: pointer;
    }
  }
  .custom-tooltip:hover {
    cursor: pointer;
  }
  .gray {
    color: #666666 !important
  }
  td + td, td[colspan="2"] {
    word-break: break-all;
  }
</style>
